import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { BugReportModal } from './BugReportModal';
import AIChatAssistant from './AIChatAssistant';
import ErrorBoundary from './ErrorBoundary';
import { getAlerts } from '../lib/api';
import {
    LayoutDashboard, Building2, Settings, Shield, LogOut, Menu, X, Bell,
    Bug, Sun, Moon, Activity, ArrowLeft
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';

const mspNavItems = [
    { to: '/msp', icon: LayoutDashboard, label: 'MSP Dashboard', end: true },
    { to: '/msp/clients', icon: Building2, label: 'Clients' },
    { to: '/msp/settings', icon: Settings, label: 'MSP Settings' },
];

export const MSPLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const [bugReportOpen, setBugReportOpen] = React.useState(false);
    const [chatOpen, setChatOpen] = React.useState(false);
    const [unreadAlerts, setUnreadAlerts] = useState(0);
    const [recentAlerts, setRecentAlerts] = useState([]);

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const response = await getAlerts(1, 5);
                const openAlerts = response.items.filter(a => a.status === 'open');
                setUnreadAlerts(openAlerts.length);
                setRecentAlerts(openAlerts.slice(0, 3));
            } catch (e) {}
        };
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getInitials = (name) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
    };

    return (
        <div className="flex h-screen bg-background" data-testid="msp-layout">
            {/* Bug Report Modal */}
            <BugReportModal open={bugReportOpen} onOpenChange={setBugReportOpen} />

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
                data-testid="msp-sidebar"
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center justify-between h-16 px-6 border-b border-border">
                        <div className="flex items-center gap-2">
                            <Shield className="w-7 h-7 text-primary" />
                            <span className="font-heading text-xl font-bold tracking-tight text-foreground">
                                SecureSight
                            </span>
                        </div>
                        <button
                            className="lg:hidden p-1 hover:bg-accent rounded"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Back to Operations */}
                    <div className="px-3 pt-4 pb-2">
                        <NavLink
                            to="/dashboard"
                            className="group flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                            Back to Operations
                        </NavLink>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
                        {mspNavItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={({ isActive }) =>
                                    `group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                                        isActive
                                            ? 'bg-primary text-primary-foreground'
                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                    }`
                                }
                                onClick={() => setSidebarOpen(false)}
                            >
                                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* User Info */}
                    <div className="p-4 border-t border-border">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                                    {getInitials(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user?.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-8">
                    <button
                        className="lg:hidden p-2 hover:bg-accent rounded-md"
                        onClick={() => setSidebarOpen(true)}
                        data-testid="mobile-menu-btn"
                    >
                        <Menu className="w-5 h-5" />
                    </button>

                    <div className="flex-1" />

                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                            data-testid="theme-toggle-btn"
                        >
                            {theme === 'light' ? (
                                <Moon className="w-5 h-5" strokeWidth={1.5} />
                            ) : (
                                <Sun className="w-5 h-5" strokeWidth={1.5} />
                            )}
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setBugReportOpen(true)}
                            title="Report a Bug"
                            data-testid="bug-report-btn"
                        >
                            <Bug className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
                        </Button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="relative" title="Notifications" data-testid="notifications-btn">
                                    <Bell className="w-5 h-5" strokeWidth={1.5} />
                                    {unreadAlerts > 0 && (
                                        <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" />
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-72">
                                <DropdownMenuLabel className="flex items-center gap-2">
                                    <Bell className="w-4 h-4" />
                                    Notifications {unreadAlerts > 0 && `(${unreadAlerts})`}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {recentAlerts.length > 0 ? (
                                    <div className="max-h-60 overflow-y-auto">
                                        {recentAlerts.map((alert) => (
                                            <DropdownMenuItem key={alert.id} onClick={() => navigate('/alerts')} className="flex flex-col items-start gap-1 py-2">
                                                <span className="font-medium text-sm">{alert.title}</span>
                                                <span className="text-xs text-muted-foreground">{alert.severity} - {alert.description?.slice(0, 60)}</span>
                                            </DropdownMenuItem>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                                        No new notifications
                                    </div>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => { navigate('/alerts'); }} data-testid="notifications-view-all">
                                    <Activity className="w-4 h-4 mr-2" />
                                    View all alerts
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                                    <Avatar className="h-8 w-8">
                                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                            {getInitials(user?.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setBugReportOpen(true)} data-testid="bug-report-menu-item">
                                    <Bug className="w-4 h-4 mr-2" />
                                    Report a Bug
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/msp/settings')} data-testid="settings-menu-item">
                                    <Settings className="w-4 h-4 mr-2" />
                                    MSP Settings
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 overflow-auto p-4 lg:p-8">
                    <ErrorBoundary>
                        {children}
                    </ErrorBoundary>
                </main>
            </div>

            {/* AI Chat Assistant Widget */}
            <AIChatAssistant
                minimized={!chatOpen}
                onToggle={() => setChatOpen(!chatOpen)}
            />
        </div>
    );
};
